import React, { useCallback, useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { Tooltip } from '@material-ui/core';
import { GripVertical, PlayerStop, Volume, ListCheck, Edit, Trash } from 'tabler-icons-react';
import { useLlamadas } from './LlamadasContext';

// Compone el ref de dnd-kit con custom properties CSS en vez de un prop `style`,
// para respetar react/forbid-dom-props (sin estilos inline en el proyecto).
function useDragTransformRef(transform, transition) {
    const nodeRef = useRef(null);
    const setRefs = useCallback((node) => {
        nodeRef.current = node;
    }, []);

    useEffect(() => {
        const node = nodeRef.current;
        if (!node) return;
        node.style.setProperty('--llam-tx', `${transform?.x ?? 0}px`);
        node.style.setProperty('--llam-ty', `${transform?.y ?? 0}px`);
        node.style.setProperty('--llam-transition', transition ?? 'none');
    }, [transform, transition]);

    return [nodeRef, setRefs];
}

function LlamadaCard({ llamada, posicion, disabled }) {
    const { abrirEdicion, setDeleteConfirm } = useLlamadas();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: llamada.tipoLlamada,
        disabled,
    });
    const [, setDragRef] = useDragTransformRef(transform, transition);

    const combinedRef = useCallback((node) => {
        setNodeRef(node);
        setDragRef(node);
    }, [setNodeRef, setDragRef]);

    return (
        <div
            ref={combinedRef}
            className={`llam-card${isDragging ? ' llam-card--dragging' : ''}`}
        >
            <button
                type="button"
                className="llam-card-handle"
                aria-label={`Reordenar ${llamada.nombre}`}
                {...attributes}
                {...listeners}
            >
                <GripVertical size={18} />
            </button>
            <span className="llam-card-pos">{posicion}</span>
            <div className="llam-card-info">
                <span className="llam-card-name">{llamada.nombre}</span>
                <span className="llam-card-area">{llamada.areaAtencionName}</span>
            </div>
            <div className="llam-card-flags">
                {llamada.esDowntime && (
                    <Tooltip title="Genera paro" arrow>
                        <span className="llam-card-flag">
                            <PlayerStop size={16} />
                        </span>
                    </Tooltip>
                )}
                {llamada.esSonora && (
                    <Tooltip title="Sonora" arrow>
                        <span className="llam-card-flag">
                            <Volume size={16} />
                        </span>
                    </Tooltip>
                )}
                {llamada.requiereClasificacion && (
                    <Tooltip title="Requiere clasificación" arrow>
                        <span className="llam-card-flag">
                            <ListCheck size={16} />
                        </span>
                    </Tooltip>
                )}
            </div>
            <div className="llam-card-actions">
                <Tooltip title="Editar" arrow>
                    <button
                        type="button"
                        className="llam-card-edit"
                        aria-label={`Editar ${llamada.nombre}`}
                        onClick={() => abrirEdicion(llamada.tipoLlamada)}
                    >
                        <Edit size={18} />
                    </button>
                </Tooltip>
                <Tooltip title="Eliminar" arrow>
                    <button
                        type="button"
                        className="llam-card-delete"
                        aria-label={`Eliminar ${llamada.nombre}`}
                        onClick={() => setDeleteConfirm(llamada)}
                    >
                        <Trash size={18} />
                    </button>
                </Tooltip>
            </div>
        </div>
    );
}

export default LlamadaCard;
